/**
 * actionTrace.js — RC 1.53 Phase 4.2.1 dev-only operation trace.
 *
 * Activated ONLY by:
 *   • URL query param  ?action_trace=1
 *   • localStorage key NDORFLOW_ACTION_TRACE=true
 *
 * Production users never see these logs. The helper is intentionally
 * cheap when disabled — a single boolean read per call site — so it
 * can be sprinkled freely across the CV lifecycle.
 *
 * Strict hold honoured: this module reads NO billing primitive, NO
 * backend route, NO orchestrator state. Pure passive logging.
 */

const _LS_FLAG = 'NDORFLOW_ACTION_TRACE';

function _readEnabled() {
  if (typeof window === 'undefined') return false;
  try {
    const sp = new URLSearchParams(window.location.search);
    if (sp.get('action_trace') === '1') return true;
  } catch { /* SSR / no URL */ }
  try {
    if (window.localStorage?.getItem(_LS_FLAG) === 'true') return true;
  } catch { /* private mode */ }
  return false;
}

let _cachedEnabled = null;
export function isTraceEnabled() {
  if (_cachedEnabled === null) _cachedEnabled = _readEnabled();
  return _cachedEnabled;
}

/** Test-only — re-read the flag on demand. */
export function _resetTraceCacheForTests() {
  _cachedEnabled = null;
}

/**
 * Log a structured trace event. NO-OP when the flag is disabled.
 *
 *   trace('cv_variant', 'dispatch_start', { jobId, idempotencyKey })
 *
 * Output shape: `[NDOR_TRACE cv_variant dispatch_start] {…}` so it
 * is easy to grep in operator console captures.
 */
export function trace(actionCode, stage, payload) {
  if (!isTraceEnabled()) return;
  const tag = `[NDOR_TRACE ${actionCode || 'unknown'} ${stage}]`;
  try {
    // Stringify defensively — operator dev consoles may not pretty-
    // print circular refs.
    const safe = payload ? JSON.parse(JSON.stringify(payload, _replaceUndef)) : null;
    // eslint-disable-next-line no-console
    console.info(tag, safe);
  } catch (e) {
    // eslint-disable-next-line no-console
    console.info(tag, '[unserialisable payload]', String(e?.message || e));
  }
}

function _replaceUndef(_k, v) {
  if (v === undefined) return null;
  return v;
}
